import { boundingBoxSize } from './rotatedArea';
/**
 * "Selecionar Área" — turning a rectangle the operator dragged into a crop.
 *
 * The drag happens on an overlay the size of the cropper's container. Inside that container the
 * cropper shows a letterboxed CROP WINDOW at the current aspect ratio, and that window in turn
 * shows a region of the padded image (what react-easy-crop reports as `croppedAreaPixels`). A
 * selection therefore has to travel: container pixels → crop-window pixels → image pixels.
 *
 * Three things were wrong with the version this replaces:
 *   - it measured against a hardcoded 3/2 window while the cropper was showing `aspectRatio`, so
 *     on a 4/5 identity photo the selected region and the saved region were different rectangles;
 *   - it discarded the operator's rectangle and squeezed the result back into the modal's fixed
 *     ratio, so "select this part" never selected that part;
 *   - it set ZOOM, so releasing the mouse jumped the image to a new scale. Zoom belongs to the
 *     zoom control and the scroll wheel; a selection says WHERE, not HOW BIG.
 *
 * The return type carries no zoom at all — that is deliberate, and it is what stops the third one
 * being wired back up by accident.
 */

export interface SelectionRect {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

export interface CropArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface SelectionCropInput {
  /** The rectangle dragged on the overlay, in container pixels. */
  selection: SelectionRect;
  /** The overlay / cropper container. */
  container: { width: number; height: number };
  /** The aspect ratio the cropper is CURRENTLY showing. */
  aspect: number;
  /** The region of the padded image currently visible, as react-easy-crop reports it. */
  croppedAreaPixels: CropArea | null | undefined;
}

export interface SelectionCrop {
  /** The selected region, in padded-image pixels. */
  area: CropArea;
  /** The ratio the OPERATOR drew. The cropper adopts it; it is their decision, not the modal's. */
  aspect: number;
}

/** Below this a drag is a click, and a click must not collapse the crop to a few pixels. */
const MIN_DRAG_PX = 10;

/**
 * Where the cropper's crop window sits inside its container.
 *
 * The window is letterboxed at the CURRENT aspect, so every conversion in this file — drag
 * in, marker out — has to agree on it. It was written out four times; one copy keeps them
 * from drifting apart, which is the kind of drift that makes a selection land somewhere
 * other than where it was drawn.
 */
function cropWindow(container: { width: number; height: number }, aspect: number) {
  let cropWinW: number;
  let cropWinH: number;
  if (container.width / container.height >= aspect) {
    cropWinH = container.height;
    cropWinW = container.height * aspect;
  } else {
    cropWinW = container.width;
    cropWinH = container.width / aspect;
  }
  return {
    cropWinW,
    cropWinH,
    cropWinX: (container.width - cropWinW) / 2,
    cropWinY: (container.height - cropWinH) / 2,
  };
}

/**
 * The one region that is both kept bright and outlined in blue.
 *
 * There used to be two: react-easy-crop dimmed around ITS crop window while the overlay drew
 * a marker somewhere else, so the bright area and the blue rectangle disagreed about what was
 * being kept. Everything that masks or outlines now asks this, so they cannot drift apart.
 *
 * Default is the crop area — letterboxed in the container at the current aspect. Once the
 * operator has drawn one with "Selecionar Área" it is theirs, unchanged: the aspect no longer
 * has a say, or "select this part" would select a different part.
 */
export function maskRect(input: {
  /** The drawn selection, with the container it was drawn in. Null before one exists. */
  frame: { rect: SelectionRect; container: { width: number; height: number } } | null;
  /** The container as it is NOW — the frame is scaled into it, so resizing keeps up. */
  container: { width: number; height: number };
  aspect: number;
}): ContainerRect {
  const { frame, container, aspect } = input;

  if (!frame) {
    const { cropWinX, cropWinY, cropWinW, cropWinH } = cropWindow(container, aspect);
    return { x: cropWinX, y: cropWinY, width: cropWinW, height: cropWinH };
  }

  // Dragging right-to-left or upwards is the same rectangle.
  const left = Math.min(frame.rect.x1, frame.rect.x2);
  const top = Math.min(frame.rect.y1, frame.rect.y2);
  const width = Math.abs(frame.rect.x2 - frame.rect.x1);
  const height = Math.abs(frame.rect.y2 - frame.rect.y1);

  // A container measured before layout settles reports 0; scaling by it would yield NaN and
  // erase the marker entirely rather than merely misplacing it.
  const sx = frame.container.width > 0 ? container.width / frame.container.width : 1;
  const sy = frame.container.height > 0 ? container.height / frame.container.height : 1;

  return { x: left * sx, y: top * sy, width: width * sx, height: height * sy };
}

export function selectionToCrop(input: SelectionCropInput): SelectionCrop | null {
  const { selection, container, aspect, croppedAreaPixels } = input;
  if (!croppedAreaPixels) return null;

  const drawnW = Math.abs(selection.x2 - selection.x1);
  const drawnH = Math.abs(selection.y2 - selection.y1);
  if (drawnW < MIN_DRAG_PX || drawnH < MIN_DRAG_PX) return null;

  const { cropWinX, cropWinY, cropWinW, cropWinH } = cropWindow(container, aspect);

  // Clamp to the window: operators overshoot, and pixels outside it are not in the photo.
  const drawnX = Math.min(selection.x1, selection.x2);
  const drawnY = Math.min(selection.y1, selection.y2);
  const left = Math.max(drawnX - cropWinX, 0);
  const top = Math.max(drawnY - cropWinY, 0);
  const right = Math.min(drawnX + drawnW - cropWinX, cropWinW);
  const bottom = Math.min(drawnY + drawnH - cropWinY, cropWinH);

  const selW = right - left;
  const selH = bottom - top;
  if (selW < MIN_DRAG_PX || selH < MIN_DRAG_PX) return null;

  return {
    area: {
      x: croppedAreaPixels.x + (left / cropWinW) * croppedAreaPixels.width,
      y: croppedAreaPixels.y + (top / cropWinH) * croppedAreaPixels.height,
      width: (selW / cropWinW) * croppedAreaPixels.width,
      height: (selH / cropWinH) * croppedAreaPixels.height,
    },
    // From the CLAMPED rectangle: what the operator actually ended up selecting, which is what
    // they can see, rather than the part of the drag that went off the photo.
    aspect: selW / selH,
  };
}

export interface ContainerRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Where a selected region sits on screen right now — the inverse of [selectionToCrop].
 *
 * The selection used to CUT: the chosen region replaced the working image, so everything around it
 * became black padding and the operator lost the context they were working from. Marking the
 * region instead means the image is never touched — but the marker then has to follow the photo as
 * it is panned and zoomed, which is what this is for.
 *
 * Deliberately NOT clamped to the container: a region scrolled partly out of view must report
 * where it actually is, so the caller can clip it when drawing. Clamping would quietly shrink the
 * marker and misrepresent what is selected.
 */
export function cropAreaToContainerRect(input: {
  area: CropArea;
  container: { width: number; height: number };
  aspect: number;
  croppedAreaPixels: CropArea | null | undefined;
}): ContainerRect | null {
  const { area, container, aspect, croppedAreaPixels } = input;
  if (!croppedAreaPixels || croppedAreaPixels.width <= 0 || croppedAreaPixels.height <= 0) {
    return null;
  }

  const { cropWinX, cropWinY, cropWinW, cropWinH } = cropWindow(container, aspect);

  return {
    x: cropWinX + ((area.x - croppedAreaPixels.x) / croppedAreaPixels.width) * cropWinW,
    y: cropWinY + ((area.y - croppedAreaPixels.y) / croppedAreaPixels.height) * cropWinH,
    width: (area.width / croppedAreaPixels.width) * cropWinW,
    height: (area.height / croppedAreaPixels.height) * cropWinH,
  };
}

export interface ContainerPoint {
  x: number;
  y: number;
}


/**
 * The four corners of a selection, on screen, with the photo at its current angle.
 *
 * Takes an axis-aligned AREA, so it can only describe a selection whose rectangle is upright in
 * image space — true at right angles, false everywhere else. [imageQuadToContainerPoints] is the
 * lossless form and is what the modal uses; this one is kept for callers that genuinely hold an
 * area rather than a quad.
 *
 * Corners come back in image order: top-left, top-right, bottom-left, bottom-right.
 */
export function cropQuadToContainerPoints(input: {
  /** The selection, in the image's own coordinates. */
  area: CropArea;
  /** The image's own size, unrotated. */
  image: { width: number; height: number };
  rotation: number;
  container: { width: number; height: number };
  aspect: number;
  croppedAreaPixels: CropArea | null | undefined;
}): ContainerPoint[] | null {
  const { area, image, rotation, container, aspect, croppedAreaPixels } = input;
  if (!croppedAreaPixels || croppedAreaPixels.width <= 0 || croppedAreaPixels.height <= 0) {
    return null;
  }

  const { cropWinX, cropWinY, cropWinW, cropWinH } = cropWindow(container, aspect);

  const radians = (rotation * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  const box = boundingBoxSize(image.width, image.height, rotation);

  const corners: Array<[number, number]> = [
    [area.x, area.y],
    [area.x + area.width, area.y],
    [area.x, area.y + area.height],
    [area.x + area.width, area.y + area.height],
  ];

  return corners.map(([x, y]) => {
    // Image space → the rotated canvas the cropper measures in…
    const dx = x - image.width / 2;
    const dy = y - image.height / 2;
    const bx = box.width / 2 + dx * cos - dy * sin;
    const by = box.height / 2 + dx * sin + dy * cos;
    // …then that canvas → the crop window on screen.
    return {
      x: cropWinX + ((bx - croppedAreaPixels.x) / croppedAreaPixels.width) * cropWinW,
      y: cropWinY + ((by - croppedAreaPixels.y) / croppedAreaPixels.height) * cropWinH,
    };
  });
}
